import React, { useCallback, useEffect, useState } from 'react';
import { Crown, Check, Coins, X } from 'lucide-react';
import { Modal, ModalContent } from '../shared/Modal';
import { Button } from '../shared/Button';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  LAUNCH_PRICING_PREVIEW,
  PAYMENTS_ENABLED,
  PRICING,
  REFUND_DAYS,
  TIER_LIMITS,
  formatPrice,
} from '../../config/constants';
import {
  createCheckoutSession,
  getLaunchPricingAvailable,
  LaunchPricingUnavailableError,
} from '../../services/stripeService';

type LimitType = 'investments' | 'bankAccounts' | 'retirement401kHoldings' | 'privateEquityHoldings' | 'cryptoHoldings' | 'general';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  limitType: LimitType;
  /**
   * The user already chose Pro (on the pricing page), so there is no
   * "Maybe Later" button: a small close icon in the corner is the only way out.
   */
  chosePro?: boolean;
}

const PRO_FEATURES = [
  'Unlimited accounts & holdings',
  'Full 50-year retirement projection',
  'Unlimited net worth history',
  'Full charts & analytics',
  'PDF reports',
  'Priority support',
];

export const UpgradeModal: React.FC<UpgradeModalProps> = ({
  isOpen,
  onClose,
  limitType,
  chosePro = false,
}) => {
  const { darkMode } = useTheme();
  const { user } = useAuth();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [launchAvailable, setLaunchAvailable] = useState(LAUNCH_PRICING_PREVIEW);

  // Only ever show the early adopter price if Stripe can still honor it.
  useEffect(() => {
    if (!isOpen || !PAYMENTS_ENABLED || LAUNCH_PRICING_PREVIEW) return;
    let cancelled = false;
    getLaunchPricingAvailable().then((available) => {
      if (!cancelled) setLaunchAvailable(available);
    });
    return () => { cancelled = true; };
  }, [isOpen]);

  const limitMessages: Record<LimitType, string> = {
    investments: `You've reached the free tier limit of ${TIER_LIMITS.free.investments} investments.`,
    bankAccounts: `You've reached the free tier limit of ${TIER_LIMITS.free.bankAccounts} bank accounts.`,
    retirement401kHoldings: `You've reached the free tier limit of ${TIER_LIMITS.free.retirement401kHoldings} 401k holdings.`,
    privateEquityHoldings: `You've reached the free tier limit of ${TIER_LIMITS.free.privateEquityHoldings} private equity holdings.`,
    cryptoHoldings: `You've reached the free tier limit of ${TIER_LIMITS.free.cryptoHoldings} crypto holdings.`,
    general: 'Unlock the full retirement projection, unlimited accounts, full charts and PDF reports.',
  };

  const handleUpgrade = useCallback(async () => {
    if (!user?.id || !user?.email) {
      setError('Please sign in to upgrade.');
      return;
    }
    setIsRedirecting(true);
    setError(null);
    try {
      const url = await createCheckoutSession(user.email, launchAvailable);
      window.location.href = url;
    } catch (err) {
      if (err instanceof LaunchPricingUnavailableError) {
        // Re-render at the regular price and make the user confirm again.
        // Never hand them off to Stripe showing a price they didn't agree to.
        setLaunchAvailable(false);
        setError(
          `Early adopter pricing just ended. Pro is ${formatPrice(PRICING.pro)}. Press Get Pro again to continue at that price.`
        );
      } else {
        // A TypeError here means the request never reached the server
        // (offline, blocked). The browser's "Failed to fetch" means nothing
        // to a customer.
        setError(
          err instanceof TypeError
            ? "Couldn't reach checkout. Check your connection and try again."
            : err instanceof Error
              ? err.message
              : 'Failed to start checkout'
        );
      }
      setIsRedirecting(false);
    }
  }, [user, launchAvailable]);

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalContent gradient>
        <div className="relative text-center">
          {chosePro && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className={`absolute -top-2 -right-2 p-2 rounded-full transition-colors ${
                darkMode ? 'text-white/50 hover:text-white hover:bg-white/10' : 'text-slate-400 hover:text-slate-700 hover:bg-slate-900/5'
              }`}
            >
              <X size={20} />
            </button>
          )}
          <div className="mb-4">
            <Crown size={48} className="mx-auto text-yellow-500" />
          </div>
          <h3 className={`text-2xl font-bold mb-3 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
            Upgrade to Pro
          </h3>
          <p className={`text-base mb-5 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
            {limitMessages[limitType]}
          </p>

          <div className={`p-4 rounded-xl ${darkMode ? 'bg-white/10' : 'bg-white/80'} mb-5`}>
            {launchAvailable && (
              <div className={`text-xs mb-1 font-semibold uppercase tracking-wide ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                Early adopter pricing
              </div>
            )}
            <div className="flex items-baseline justify-center gap-2">
              {launchAvailable && (
                <span className={`text-xl font-semibold line-through ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                  {formatPrice(PRICING.pro)}
                </span>
              )}
              <span className="text-4xl font-bold text-violet-600">
                {formatPrice(launchAvailable ? PRICING.launch : PRICING.pro)}
              </span>
            </div>
            <div className={`text-xs mt-1 mb-2 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              One-time payment. No subscription. Pro is yours for good.
            </div>
            <div className={`text-xs mb-3 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              {REFUND_DAYS}-day money-back guarantee. Pro is for US customers only.
            </div>
            <div className={`flex flex-wrap gap-x-4 gap-y-1 justify-center text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
              {PRO_FEATURES.map(f => (
                <span key={f} className="flex items-center gap-1">
                  <Check size={14} className={darkMode ? 'text-violet-400' : 'text-violet-600'} />
                  {f}
                </span>
              ))}
            </div>
          </div>

          {error && (
            <div className={`mb-3 text-sm rounded-lg px-3 py-2 ${
              darkMode ? 'bg-red-500/10 text-red-300' : 'bg-red-50 text-red-600'
            }`}>
              {error}
            </div>
          )}

          <Button
            variant="primary"
            fullWidth
            disabled={!PAYMENTS_ENABLED || isRedirecting}
            onClick={PAYMENTS_ENABLED ? handleUpgrade : undefined}
            className={`mb-3 ${PAYMENTS_ENABLED ? '' : 'opacity-75'}`}
          >
            {!PAYMENTS_ENABLED ? 'Coming Soon' : isRedirecting ? 'Redirecting…' : (
              <span className="flex items-center justify-center gap-2">
                <Coins size={18} />
                {`Get Pro for ${formatPrice(launchAvailable ? PRICING.launch : PRICING.pro)}`}
              </span>
            )}
          </Button>
          {!chosePro && (
            <Button
              variant="secondary"
              fullWidth
              onClick={onClose}
            >
              Maybe Later
            </Button>
          )}
        </div>
      </ModalContent>
    </Modal>
  );
};
interface UpgradePromptProps {
  count: number;
  type: 'accounts' | 'investments' | 'holdings';
  onClick: () => void;
}

export const UpgradePrompt: React.FC<UpgradePromptProps> = ({
  count,
  type,
  onClick,
}) => {
  const { darkMode } = useTheme();

  return (
    <div
      onClick={onClick}
      className={`
        p-3 rounded-lg cursor-pointer transition-all
        ${darkMode
          ? 'bg-violet-600/20 border border-violet-500/50 hover:bg-violet-600/30'
          : 'bg-violet-100 border border-violet-300 hover:bg-violet-200'
        }
      `}
    >
      <div className="flex items-center justify-between">
        <span className={`text-sm font-semibold ${darkMode ? 'text-violet-300' : 'text-violet-700'}`}>
          +{count} more {type}
        </span>
        <Crown size={16} className="text-yellow-500" />
      </div>
      <p className={`text-xs mt-1 ${darkMode ? 'text-violet-400' : 'text-violet-600'}`}>
        Upgrade to Pro to unlock
      </p>
    </div>
  );
};

interface UpgradeChartsCTAProps {
  onClick: () => void;
}

export const UpgradeChartsCTA: React.FC<UpgradeChartsCTAProps> = ({ onClick }) => {
  const { darkMode } = useTheme();

  return (
    <div
      onClick={onClick}
      className={`
        p-4 rounded-xl cursor-pointer transition-all text-center
        ${darkMode
          ? 'bg-violet-600/20 border border-violet-500/50 hover:bg-violet-600/30'
          : 'bg-violet-100 border border-violet-300 hover:bg-violet-200'
        }
      `}
    >
      <Crown size={24} className="mx-auto mb-2 text-yellow-500" />
      <p className={`text-sm font-semibold ${darkMode ? 'text-violet-300' : 'text-violet-700'} mb-1`}>
        Unlock Advanced Charts & Reports
      </p>
      <p className={`text-xs ${darkMode ? 'text-violet-400' : 'text-violet-600'}`}>
        Historical trends * PDF reports * More insights
      </p>
    </div>
  );
};
